import React from "react";
import { Typography } from "@RHCommerceDev/unstyled-ui-components";
import { cn } from "@RHCommerceDev/utils/cn";
import he from "he";
import {
  countries,
  countryAbbreviationMapper
} from "@RHCommerceDev/resources/countries-config.json";
import HamburgerCountrySelector from "./HamburgerCountrySelector";
import { groupChildCategories } from "@RHCommerceDev/utils/parseCatalogNavigationData";
import { CATALOG_CATEGORIES } from "@RHCommerceDev/utils/constants";
import ArrowBack from "@RHCommerceDev/icon-arrow-back";

/*
 * These are a presentation components responsible solely for rendering the columns UI.It should not contain any business logic.
 */

type PrimaryColumnProps = {
  sections: (NavigationElement | null | undefined)[];
  selectedItem: {
    sectionIndex: number;
    primaryIndex: number;
    secondaryIndex: number;
  };
  onPrimaryItemClick: (
    event: React.MouseEvent<HTMLButtonElement>,
    sectionIndex: number,
    primaryIndex: number
  ) => void;
  onCountrySelectorClick: () => void;
  selectedCountry: string;
  selectedLanguage: string;
  isCountrySelectorOpen: boolean;
};

type SecondaryColumnProps = {
  sections: (NavigationElement | null | undefined)[];
  selectedItem: {
    sectionIndex: number;
    primaryIndex: number;
    secondaryIndex: number;
  };
  onSecondaryItemClick: (
    event: React.MouseEvent<HTMLButtonElement>,
    secondaryIndex: number
  ) => void;
  onSecondaryBackButtonClick?: () => void;
  selectedCountry: string;
  selectedLanguage: string;
  isCountrySelectorOpen: boolean;
  variant: "desktop" | "mobile";
  waterWorksUrl?: string;
};

type TertiaryColumnProps = {
  sections: (NavigationElement | null | undefined)[];
  selectedItem: {
    sectionIndex: number;
    primaryIndex: number;
    secondaryIndex: number;
  };
  groups: ReturnType<typeof groupChildCategories>;
  onTertiaryItemClick: (
    event: React.MouseEvent<HTMLButtonElement>,
    groupIndex: number,
    tertiaryIndex: number
  ) => void;
  onTertiaryBackButtonClick?: () => void;
  variant: "desktop" | "mobile";
  waterWorksUrl?: string;
};

export const PrimaryColumn = ({
  sections,
  selectedItem,
  onPrimaryItemClick,
  onCountrySelectorClick,
  selectedCountry,
  selectedLanguage,
  isCountrySelectorOpen
}: PrimaryColumnProps) => {
  return (
    <div
      className={cn(
        "flex flex-col items-start",
        "md:w-[202px] lg:w-[248px]",
        "gap-12 md:gap-10 lg:gap-12",
        "mt-4 sm:mt-0",
        "md:pr-5 lg:pr-6",
        "md:border-r-[0.5px] md:border-r-rh-warm-latte",
        "overflow-y-auto overflow-x-clip",
        "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      )}
    >
      {sections?.map((section, sectionIndex) => (
        <div
          key={sectionIndex}
          className={cn(
            "flex flex-col items-start justify-start",
            "gap-5 sm:gap-6 md:gap-4 lg:gap-5"
          )}
        >
          {/* Section Title */}
          <Typography
            variant="default"
            fontFamily="rhcRoman"
            fontWeight="normal"
            className="text-left text-base uppercase leading-normal tracking-wider md:text-[13px] lg:text-base"
            dangerouslySetInnerHTML={{
              __html: he.decode(section?.displayName ?? "")
            }}
          />

          {/* Section Content */}
          {section?.childCategories?.map((primaryItem, primaryIndex) => {
            const isSelected =
              selectedItem?.primaryIndex === primaryIndex &&
              selectedItem?.sectionIndex === sectionIndex;

            return (
              <button
                key={primaryIndex}
                data-analytics-rootcat={section?.displayName}
                onClick={e => {
                  onPrimaryItemClick?.(e, sectionIndex, primaryIndex);
                }}
                className="p-0 text-inherit"
              >
                <Typography
                  variant="default"
                  fontFamily={isSelected ? "rhcRoman" : "rhc"}
                  fontWeight={isSelected ? "normal" : "thin"}
                  className={cn(
                    "text-left text-base uppercase leading-normal md:text-[13px] lg:text-base",
                    isSelected ? "tracking-wide" : "tracking-wider"
                  )}
                  dangerouslySetInnerHTML={{
                    __html: he.decode(primaryItem?.displayName ?? "")
                  }}
                />
              </button>
            );
          })}

          {/* Country Selector Button */}
          {sectionIndex === 0 && (
            <button
              onClick={() => {
                onCountrySelectorClick?.();
              }}
              className={cn(
                "relative flex w-full items-center justify-start",
                "p-0 py-5 sm:py-6 md:py-4",
                "xs:mt-7 sm:mt-6 md:mt-6 lg:mt-7",
                "text-inherit",
                "before:absolute before:top-0 before:h-[0.5px] before:w-screen before:bg-rh-warm-latte before:content-[''] md:before:w-[162px] lg:before:w-[167px]",
                "after:absolute after:bottom-0 after:h-[0.5px] after:w-screen after:bg-rh-warm-latte after:content-[''] md:after:w-[162px] lg:after:w-[167px]"
              )}
            >
              <Typography
                variant="default"
                fontFamily={isCountrySelectorOpen ? "rhcRoman" : "rhc"}
                fontWeight={isCountrySelectorOpen ? "normal" : "thin"}
                className={cn(
                  "text-left text-base uppercase leading-[50px] md:text-[13px] lg:text-base",
                  isCountrySelectorOpen ? "tracking-wide" : "tracking-[0.01em]"
                )}
              >
                {
                  countryAbbreviationMapper[
                    selectedCountry === "MC"
                      ? "FR"
                      : (selectedCountry as keyof typeof countryAbbreviationMapper)
                  ]
                }
                {countries[
                  selectedCountry === "MC"
                    ? "FR"
                    : (selectedCountry as keyof typeof countries)
                ].label?.match(/^(.+?)\s*(\([^)]+\))?$/)?.[2] ?? ""}{" "}
                {selectedLanguage}
              </Typography>
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export const SecondaryColumn = ({
  sections,
  selectedItem,
  onSecondaryItemClick,
  onSecondaryBackButtonClick,
  selectedCountry,
  selectedLanguage,
  isCountrySelectorOpen,
  variant,
  waterWorksUrl
}: SecondaryColumnProps) => {
  const selectedSection = sections?.[selectedItem.sectionIndex];
  const selectedPrimaryItem =
    selectedSection?.childCategories?.[selectedItem.primaryIndex];

  return (
    <div
      className={cn(
        "flex flex-col items-start justify-start",
        "mt-4 sm:mt-0 md:mt-9 lg:mt-11",
        "overflow-y-auto",
        "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      )}
    >
      {/* Back button for mobile drawer*/}
      {variant === "mobile" && (
        <button
          onClick={() => {
            onSecondaryBackButtonClick?.();
          }}
          className="mb-6 flex items-center p-0 text-inherit sm:mb-[30px]"
        >
          <ArrowBack className="mr-4" />
          <Typography
            variant="default"
            fontFamily="rhcRoman"
            fontWeight="normal"
            className="text-left text-base uppercase leading-normal tracking-wider"
          >
            {isCountrySelectorOpen ? (
              <>
                {
                  countryAbbreviationMapper[
                    selectedCountry === "MC"
                      ? "FR"
                      : (selectedCountry as keyof typeof countryAbbreviationMapper)
                  ]
                }
                {countries[
                  selectedCountry === "MC"
                    ? "FR"
                    : (selectedCountry as keyof typeof countries)
                ].label?.match(/^(.+?)\s*(\([^)]+\))?$/)?.[2] ?? ""}{" "}
                {selectedLanguage.split("-")[0].toUpperCase()}
              </>
            ) : (
              selectedPrimaryItem?.displayName
            )}
          </Typography>
        </button>
      )}

      {/* Column Content*/}
      {isCountrySelectorOpen ? (
        <HamburgerCountrySelector />
      ) : (
        <div
          className={cn(
            "flex flex-col items-start justify-start",
            "gap-6 md:gap-4 lg:gap-5"
          )}
        >
          {selectedPrimaryItem?.childCategories?.map(
            (secondaryItem, secondaryIndex) => {
              const isSelected = selectedItem.secondaryIndex === secondaryIndex;

              return (
                <button
                  data-analytics-parentCat={secondaryItem?.displayName}
                  key={secondaryIndex}
                  onClick={e => {
                    onSecondaryItemClick(e, secondaryIndex);
                  }}
                  className="p-0 text-inherit"
                >
                  {secondaryItem?.displayName?.includes(
                    CATALOG_CATEGORIES.WATERWORKS
                  ) ? (
                    <img
                      src={waterWorksUrl}
                      alt={secondaryItem?.displayName ?? ""}
                      width={188}
                      height={16}
                      className="h-4 w-[188px]"
                    />
                  ) : (
                    <Typography
                      variant="default"
                      fontFamily={isSelected ? "rhcRoman" : "rhc"}
                      fontWeight={isSelected ? "normal" : "thin"}
                      className={cn(
                        "text-left text-base uppercase leading-normal md:whitespace-nowrap md:text-[13px] lg:text-base",
                        isSelected ? "tracking-wide" : "tracking-wider"
                      )}
                      dangerouslySetInnerHTML={{
                        __html: he.decode(secondaryItem?.displayName ?? "")
                      }}
                    />
                  )}
                </button>
              );
            }
          )}
        </div>
      )}
    </div>
  );
};

export const TertiaryColumn = ({
  sections,
  selectedItem,
  groups,
  onTertiaryItemClick,
  onTertiaryBackButtonClick,
  variant,
  waterWorksUrl
}: TertiaryColumnProps) => {
  const selectedSection = sections?.[selectedItem.sectionIndex];
  const selectedPrimaryItem =
    selectedSection?.childCategories?.[selectedItem.primaryIndex];
  const selectedSecondaryItem =
    selectedPrimaryItem?.childCategories?.[selectedItem.secondaryIndex];

  return (
    <div
      className={cn(
        "md:overflow-x-auto",
        "md:columns-2",
        "md:gap-x-[75px] lg:gap-x-20",
        "mt-4 sm:mt-0 md:mt-9 lg:mt-11",
        "[-ms-overflow-style:none] [column-fill:_auto] [scrollbar-width:none]"
      )}
    >
      {/* Back button for mobile */}
      {variant === "mobile" && (
        <button
          onClick={() => {
            onTertiaryBackButtonClick?.();
          }}
          className="mb-6 flex items-center p-0 text-inherit sm:mb-[30px]"
        >
          <ArrowBack className="mr-4" />
          <Typography
            variant="default"
            fontFamily="rhc"
            fontWeight="thin"
            className="text-left text-base uppercase leading-normal tracking-wider"
          >
            {selectedPrimaryItem?.displayName}
          </Typography>
          <Typography
            variant="default"
            fontFamily="rhcRoman"
            fontWeight="normal"
            className="mx-2 text-left text-base uppercase leading-normal tracking-wider"
          >
            {"|"}
          </Typography>
          {selectedSecondaryItem?.displayName?.includes(
            CATALOG_CATEGORIES.WATERWORKS
          ) ? (
            <img
              src={waterWorksUrl}
              alt={selectedSecondaryItem?.displayName ?? ""}
              width={188}
              height={16}
              className="h-4 w-[188px]"
            />
          ) : (
            <Typography
              variant="default"
              fontFamily="rhcRoman"
              fontWeight="normal"
              className="text-left text-base uppercase leading-normal tracking-wider"
            >
              {selectedSecondaryItem?.displayName}
            </Typography>
          )}
        </button>
      )}

      {/* Column Content */}
      {groups?.map((group, groupIndex) => (
        <div
          key={groupIndex}
          className={cn(
            "flex flex-col items-start justify-start",
            "gap-4 md:gap-2",
            "mb-[30px] md:mb-5",
            "break-inside-avoid" // Prevents this element from being split across multiple columns
          )}
        >
          {/* Group Header */}
          {group?.header &&
            (group?.header?.displayName?.includes(
              CATALOG_CATEGORIES.WATERWORKS
            ) ? (
              <img
                src={waterWorksUrl}
                alt={group?.header?.displayName ?? ""}
                width={188}
                height={16}
                className="h-4 w-[188px]"
              />
            ) : (
              <Typography
                variant="default"
                fontFamily="rhcRoman"
                fontWeight="normal"
                className="text-left text-base uppercase leading-normal tracking-wider md:text-[13px] lg:text-base"
                dangerouslySetInnerHTML={{
                  __html: he.decode(group.header?.displayName ?? "")
                }}
              />
            ))}

          {/* Group Content */}
          {group?.children?.map((tertiaryItem, tertiaryIndex) => (
            <button
              key={tertiaryIndex}
              onClick={e => {
                onTertiaryItemClick(e, groupIndex, tertiaryIndex);
              }}
              className="p-0 text-inherit"
            >
              {tertiaryItem?.displayName?.includes(
                CATALOG_CATEGORIES.WATERWORKS
              ) ? (
                <img
                  src={waterWorksUrl}
                  alt={tertiaryItem?.displayName ?? ""}
                  width={188}
                  height={16}
                  className="h-4 w-[188px]"
                />
              ) : (
                <Typography
                  variant="default"
                  fontFamily="rhc"
                  fontWeight="thin"
                  className="text-left text-base capitalize leading-normal tracking-[0.2px] md:text-[13px] lg:text-base"
                  dangerouslySetInnerHTML={{
                    __html: he.decode(tertiaryItem?.displayName ?? "")
                  }}
                />
              )}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
};
